import { useCallback, useState } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useLoginStateAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

import { ReportAbuseConfirmModal } from '../containers/ReportAbuseConfirmModal';
import { ReportAbuseModal } from '../containers/ReportAbuseModal';
import type { ReportContext } from '../shared/types';

export function useOpenReportAbuseModal({
	v2,
	reportContext,
}: {
	v2?: boolean;
	reportContext: ReportContext;
}): UseOpenReportAbuseModalResult {
	const loginState = useLoginStateAsync();
	const location = useLocation();
	const navigate = useNavigate();
	const track = useTracking();

	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const handleReportSuccess = useCallback(() => {
		setIsReportModalOpen(false);
		setIsConfirmModalOpen(true);
	}, []);

	const open = useCallback((): void => {
		// non-auth users must authenticate before reporting
		// so we're redirecting to the login page, with a redirection back to this page
		if (!isLoaded(loginState) || loginState.value !== 'AUTHENTICATED') {
			const redirectUrl = `/login_or_join?${qs.stringify(
				{ user_flow: 'report_abuse', redirect_to: `${location.pathname}${location.search}` },
				{ encodeValuesOnly: true },
			)}`;
			navigate(redirectUrl);
			return;
		}

		const { type } = reportContext;
		void track('report_abuse_click', {
			type,
			experiment_variation: v2 ? 'variant' : 'control',
			// This property is used to monitor the v2 experiment
			...(type === 'petition' ? { petition_id: reportContext.petitionId } : {}),
		});
		setIsReportModalOpen(true);
	}, [location, loginState, navigate, reportContext, track, v2]);

	const reportAbuseModals = (
		<>
			<ReportAbuseModal
				isOpen={isReportModalOpen}
				onOpenChange={setIsReportModalOpen}
				onSuccess={handleReportSuccess}
				v2={v2}
				reportContext={reportContext}
			/>
			<ReportAbuseConfirmModal isOpen={isConfirmModalOpen} onOpenChange={setIsConfirmModalOpen} />
		</>
	);

	return { open, reportAbuseModals };
}

type UseOpenReportAbuseModalResult = {
	open: () => void;
	reportAbuseModals: React.ReactNode;
};
